import cx from 'classnames';
import { type ChangeEvent, type ReactNode, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu } from '@base-ui/react';
import { AddCard } from '@components/addCard/AddCard';
import { Button, ButtonVariants } from '@components/button/Button';
import { Card } from '@components/card/Card';
import { FloorPlan } from '@components/floorPlan/FloorPlan';
import { floorPlanTablesRendererFactory } from '@components/floorPlan/floorPlanTablesRendererFactory';
import { Icon } from '@components/icon/Icon';
import { IconButton } from '@components/iconButton/IconButton';
import { errorToast, successToast } from '@components/toasts/Toasts';
import type { FloorPlanData } from '@shared/types/floorPlans';
import { useRestaurant } from 'restaurantAdmin/context/useRestaurant';
import { useAdminFloorPlans } from 'restaurantAdmin/hooks/useAdminFloorPlans';
import { PageContent } from 'restaurantAdmin/layout/PageContent';
import { PageHeader } from 'restaurantAdmin/layout/PageHeader';
import { FloorPlanCopyModal } from 'restaurantAdmin/operations/floorPlans/FloorPlanCopyModal';
import typography from '~styles/typography.scss';
import { duplicateFloorPlan, updateFloorPlans } from './apiHelpers';

import styles from './FloorPlansPage.scss';

const FloorPlanEditLink = ({
  children,
  restaurantId,
  floorPlanId,
}: {
  children: ReactNode;
  restaurantId: string;
  floorPlanId: string;
}) => (
  <Link
    aria-label="Edit floor plan"
    to={`/restaurants/${restaurantId}/floor-plans/${floorPlanId}/edit`}
  >
    {children}
  </Link>
);

export const FloorPlansPage = () => {
  const [openCopyFloorPlanModal, setOpenCopyFloorPlanModal] = useState(false);
  const navigate = useNavigate();
  const restaurant = useRestaurant();

  const { floorPlans, isLoading, fetchFloorPlans } = useAdminFloorPlans();

  const [editMode, setEditMode] = useState(false);
  const [floorPlansForEditing, setFloorPlansForEditing] = useState<
    FloorPlanData[]
  >([]);

  useEffect(() => {
    if (!isLoading) {
      setFloorPlansForEditing(floorPlans);
    }
  }, [isLoading, floorPlans.length]);

  const allowReordering = floorPlans.length > 1 && editMode;

  const decrementPosition = (oldIndex: number) => {
    if (!allowReordering) {
      return;
    }

    if (oldIndex === 0) {
      const newOrder = [
        ...floorPlansForEditing.slice(1),
        floorPlansForEditing[0],
      ];

      setFloorPlansForEditing(newOrder);
    } else {
      const newIndex = oldIndex - 1;
      const newOrder = floorPlansForEditing
        .toSpliced(oldIndex, 1)
        .toSpliced(newIndex, 0, floorPlansForEditing[oldIndex]);

      setFloorPlansForEditing(newOrder);
    }
  };

  const incrementPosition = (oldIndex: number) => {
    if (!allowReordering) {
      return;
    }

    if (oldIndex === floorPlansForEditing.length - 1) {
      const newOrder = [
        floorPlansForEditing[oldIndex],
        ...floorPlansForEditing.slice(0, oldIndex),
      ];

      setFloorPlansForEditing(newOrder);
    } else {
      const newIndex = oldIndex + 1;
      const newOrder = floorPlansForEditing
        .toSpliced(oldIndex, 1)
        .toSpliced(newIndex, 0, floorPlansForEditing[oldIndex]);

      setFloorPlansForEditing(newOrder);
    }
  };

  const renameFloorPlan = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    floorPlansForEditing[index].name = e.target.value;
  };

  const onCancel = () => {
    setEditMode(false);
    fetchFloorPlans();
  };

  const onSave = async () => {
    await updateFloorPlans(
      restaurant.id,
      floorPlansForEditing.map(({ id, name }) => ({ id, name })),
    );

    setEditMode(false);
    successToast({ message: 'Changes saved!' });
  };

  const handleDuplicateFloorPlan = (
    fp: FloorPlanData,
    { duplicateTables }: { duplicateTables: boolean },
  ) => {
    return async () => {
      try {
        const { id } = await duplicateFloorPlan(restaurant.id, fp.id, {
          duplicateTables,
        });
        successToast({ message: `"${fp.name}" duplicated successfully` });
        navigate(`/restaurants/${restaurant.id}/floor-plans/${id}/edit`);
      } catch {
        errorToast({ message: `Failed to duplicate "${fp.name}"` });
      }
    };
  };

  return (
    <>
      <PageHeader category="Operations" title="Floor Plans">
        {!editMode && (
          <Button
            variant={ButtonVariants.Secondary}
            onClick={() => setEditMode(true)}
            label="Edit"
          />
        )}
        {editMode && (
          <>
            <Button
              variant={ButtonVariants.Primary}
              onClick={onSave}
              label="Save"
            />
            <Button
              variant={ButtonVariants.Tertiary}
              onClick={onCancel}
              label="Cancel"
            />
          </>
        )}
      </PageHeader>

      <PageContent>
        <ul className={styles.floorPlanList}>
          {floorPlansForEditing.map((fp, i) => (
            <li key={fp.id}>
              <Card className={styles.floorPlanCard}>
                <div className={styles.primaryContent}>
                  <IconButton
                    iconName="chevronLeft"
                    className={cx({
                      [styles.reorderArrow]: true,
                      [styles.hidden]: !allowReordering,
                    })}
                    ariaLabel={`move ${fp.name} toward beginning of list`}
                    onClick={() => decrementPosition(i)}
                  />
                  <FloorPlanEditLink
                    restaurantId={restaurant.id}
                    floorPlanId={fp.id}
                  >
                    <FloorPlan
                      className={styles.floorPlan}
                      disableControls
                      backgroundSrc={fp.backgroundSrc}
                      floorPlanTablesRenderer={floorPlanTablesRendererFactory({
                        tables: fp.floorPlanTables,
                        showHoverStroke: false,
                      })}
                    />
                  </FloorPlanEditLink>
                  <IconButton
                    iconName="chevronRight"
                    className={cx({
                      [styles.reorderArrow]: true,
                      [styles.hidden]: !allowReordering,
                    })}
                    ariaLabel={`move ${fp.name} toward end of list`}
                    onClick={() => incrementPosition(i)}
                  />
                </div>

                <div className={cx(typography.c2, styles.floorPlanName)}>
                  <div>
                    <svg className={styles.floorPlanPositionContainer}>
                      <circle />
                    </svg>
                    <span
                      className={cx(styles.floorPlanPosition, typography.t2)}
                    >
                      {i + 1}
                    </span>
                    {editMode ? (
                      <input
                        data-testid={`floor-plan-${i}-name`}
                        defaultValue={fp.name}
                        onChange={(e) => renameFloorPlan(e, i)}
                        maxLength={50}
                      />
                    ) : (
                      fp.name
                    )}
                  </div>
                  <Menu.Root>
                    <Menu.Trigger
                      className={styles.duplicateMenuTrigger}
                      aria-label={`${fp.name} duplicate options`}
                    >
                      <Icon name="plus" />
                    </Menu.Trigger>
                    <Menu.Portal>
                      <Menu.Positioner
                        className={styles.duplicateMenuPositioner}
                        side="bottom"
                        sideOffset={8}
                      >
                        <Menu.Popup className={styles.duplicateMenuPopup}>
                          <Menu.Item
                            className={styles.duplicateMenuItem}
                            onClick={handleDuplicateFloorPlan(fp, {
                              duplicateTables: false,
                            })}
                          >
                            Duplicate Floor Plan
                          </Menu.Item>
                          <Menu.Item
                            className={styles.duplicateMenuItem}
                            onClick={handleDuplicateFloorPlan(fp, {
                              duplicateTables: true,
                            })}
                          >
                            Duplicate Floor Plan + Tables
                          </Menu.Item>
                          <Menu.Item className={styles.duplicateMenuItem}>
                            Cancel
                          </Menu.Item>
                        </Menu.Popup>
                      </Menu.Positioner>
                    </Menu.Portal>
                  </Menu.Root>
                </div>
              </Card>
            </li>
          ))}
          <li key={'add-floor-plan'}>
            <AddCard
              className={styles.floorPlanCard}
              label="Add Floor Plan"
              onClick={() => setOpenCopyFloorPlanModal(true)}
            />
          </li>
        </ul>

        <FloorPlanCopyModal
          floorPlans={floorPlansForEditing}
          open={openCopyFloorPlanModal}
          onClose={() => setOpenCopyFloorPlanModal(false)}
          onOptionClick={handleDuplicateFloorPlan}
        />
      </PageContent>
    </>
  );
};
